// src/components/Recruiter/AppliedCandidates.jsx
import React, { useState, useEffect } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import CVPDFTemplate from './CVPDFTemplate';
import html2pdf from 'html2pdf.js';

const AppliedCandidates = ({ jobs }) => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [contactUnlocked, setContactUnlocked] = useState(false);

    useEffect(() => {
        loadApplications();
    }, [jobs]);

    const loadApplications = async () => {
        setLoading(true);
        try {
            const allApps = [];
            for (const job of jobs) {
                if (job.applications?.length > 0) {
                    for (const uid of job.applications) {
                        const userDoc = await getDoc(doc(db, 'users', uid));
                        const candidateDoc = await getDoc(doc(db, 'candidates', uid));
                        if (userDoc.exists()) {
                            allApps.push({
                                jobId: job.id,
                                jobTitle: job.title,
                                uid: uid,
                                user: userDoc.data(),
                                candidate: candidateDoc.exists() ? candidateDoc.data() : {},
                                appliedAt: new Date().toISOString()
                            });
                        }
                    }
                }
            }
            setApplications(allApps);
        } catch (error) {
            console.error("Error loading applications:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewProfile = (app) => {
        // Merge user and candidate data
        const mergedCandidate = {
            ...app.user,
            ...app.candidate,
            id: app.uid
        };
        setSelectedCandidate(mergedCandidate);
        // Contact info is always visible for applied candidates (they applied to the job)
        setContactUnlocked(true);
        setShowProfileModal(true);
    };

    const closeProfileModal = () => {
        setShowProfileModal(false);
        setSelectedCandidate(null);
        setContactUnlocked(false);
    };

    const handleDownloadCV = async () => {
        if (!selectedCandidate) return;

        const element = document.getElementById('cv-pdf-content');
        if (!element) return;

        try {
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `CV_${selectedCandidate.firstName}_${selectedCandidate.lastName}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, letterRendering: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold text-gradpath-dark mb-4">Applied Candidates</h3>
                
                {applications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <i className="fas fa-users text-4xl text-gray-300 mb-3"></i>
                        <p>No candidates have applied to your jobs yet</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Job Applied</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Skills</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {applications.map((app, index) => (
                                    <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium">
                                            {app.user.firstName} {app.user.lastName}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{app.jobTitle}</td>
                                        <td className="px-4 py-3 text-sm">{app.candidate.city || 'N/A'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <div className="flex flex-wrap gap-1">
                                                {app.candidate.skills?.slice(0, 3).map((skill, i) => (
                                                    <span key={i} className="bg-green-50 text-gradpath-green px-2 py-0.5 rounded text-xs">
                                                        {skill}
                                                    </span>
                                                ))}
                                                {app.candidate.skills?.length > 3 && (
                                                    <span className="text-xs text-gray-400">+{app.candidate.skills.length - 3}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <button
                                                onClick={() => handleViewProfile(app)}
                                                className="text-gradpath-green hover:text-gradpath-dark text-sm font-medium flex items-center gap-1"
                                            >
                                                <i className="fas fa-eye"></i> View Profile
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Profile View Modal */}
            {selectedCandidate && showProfileModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeProfileModal}>
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={closeProfileModal}
                            className="sticky top-0 float-right m-4 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="p-6 pt-0">
                            <div className="pt-6 pb-4 border-b border-gray-200">
                                <h2 className="text-2xl font-bold text-gradpath-dark">
                                    {selectedCandidate.firstName} {selectedCandidate.lastName}
                                </h2>
                                <p>{selectedCandidate.email}</p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                        {selectedCandidate.userType === 'student' ? 'Student' : 'Graduate'}
                                    </span>
                                    <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">
                                        Applied to: {applications.find(a => a.uid === selectedCandidate.id)?.jobTitle || 'N/A'}
                                    </span>
                                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                                        Contact Visible (Applied)
                                    </span>
                                </div>
                            </div>

                            {/* Contact Information - Always visible for applied candidates */}
                            <div className="mt-6">
                                <h3 className="text-lg font-bold text-gradpath-dark mb-4">Contact Information</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-500">Phone</label>
                                        <p className="font-medium">{selectedCandidate.phoneNumber || 'Not provided'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Location</label>
                                        <p className="font-medium">{selectedCandidate.city}, {selectedCandidate.province}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Date of Birth</label>
                                        <p className="font-medium">{selectedCandidate.dateOfBirth || 'Not provided'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Gender</label>
                                        <p className="font-medium capitalize">{selectedCandidate.gender || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Citizenship</label>
                                        <p className="font-medium">{selectedCandidate.citizenship || 'Not specified'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Professional Links - Always visible for applied candidates */}
                            {(selectedCandidate.linkedin || selectedCandidate.portfolio) && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Professional Links</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {selectedCandidate.linkedin && (
                                            <div>
                                                <label className="text-sm text-gray-500">
                                                    <i className="fab fa-linkedin text-blue-600 mr-1"></i> LinkedIn
                                                </label>
                                                <p>
                                                    <a href={selectedCandidate.linkedin} target="_blank" rel="noopener noreferrer" className="text-gradpath-green hover:underline break-all">
                                                        {selectedCandidate.linkedin}
                                                    </a>
                                                </p>
                                            </div>
                                        )}
                                        {selectedCandidate.portfolio && (
                                            <div>
                                                <label className="text-sm text-gray-500">
                                                    <i className="fas fa-folder-open text-gradpath-green mr-1"></i> Portfolio
                                                </label>
                                                <p>
                                                    <a href={selectedCandidate.portfolio} target="_blank" rel="noopener noreferrer" className="text-gradpath-green hover:underline break-all">
                                                        {selectedCandidate.portfolio}
                                                    </a>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Job Preferences */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-lg font-bold text-gradpath-dark mb-4">Job Preferences</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-500">Looking For</label>
                                        <p className="font-medium">{selectedCandidate.lookingFor || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Preferred Location</label>
                                        <p className="font-medium">{selectedCandidate.preferredJobLocation || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Desired Salary</label>
                                        <p className="font-medium">{selectedCandidate.desiredSalary || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Willing to Relocate</label>
                                        <p className="font-medium">{selectedCandidate.willingToRelocate ? '✅ Yes' : '❌ No'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Skills & Experience */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-lg font-bold text-gradpath-dark mb-4">Skills & Experience</h3>
                                
                                <div className="mb-4">
                                    <label className="text-sm text-gray-500">Skills</label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {selectedCandidate.skills?.map((skill, i) => (
                                            <span key={i} className="bg-green-50 text-gradpath-green px-2 py-0.5 rounded-full text-xs">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="text-sm text-gray-500">Languages</label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {selectedCandidate.languages?.map((lang, i) => (
                                            <span key={i} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full text-xs">
                                                {lang}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm text-gray-500">Experience Level</label>
                                    <p className="font-medium">{selectedCandidate.experienceLevel || 'Not specified'}</p>
                                </div>
                            </div>

                            {/* Professional Summary */}
                            {selectedCandidate.professionalSummary && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Professional Summary</h3>
                                    <p className="text-gray-600">{selectedCandidate.professionalSummary}</p>
                                </div>
                            )}

                            {/* Education - UPDATED with new fields */}
                            {selectedCandidate.education?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Education</h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.education.map((edu, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{edu.qualification}</p>
                                                <p className="text-sm text-gray-600">{edu.institution}</p>
                                                {(edu.startDate || edu.endDate) && (
                                                    <p className="text-sm text-gray-500">
                                                        {edu.startDate || 'Start date not specified'} 
                                                        {edu.isCurrent ? ' - Present' : edu.endDate ? ` - ${edu.endDate}` : ''}
                                                        {edu.isCurrent && (
                                                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                                Current
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                                {edu.keyHighlights && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        <span className="font-medium">Highlights:</span> {edu.keyHighlights}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Work Experience */}
                            {selectedCandidate.workExperience?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Work Experience</h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.workExperience.map((exp, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{exp.title} at {exp.company}</p>
                                                <p className="text-sm text-gray-500">{exp.startDate} - {exp.endDate || 'Present'}</p>
                                                {exp.description && <p className="text-sm text-gray-600 mt-1">{exp.description}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Certifications & Awards - NEW */}
                            {selectedCandidate.certifications?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">
                                        <i className="fas fa-certificate text-gradpath-green mr-2"></i>
                                        Certifications & Awards
                                    </h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.certifications.map((cert, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{cert.name}</p>
                                                {cert.issuingOrganization && (
                                                    <p className="text-sm text-gray-600">{cert.issuingOrganization}</p>
                                                )}
                                                {cert.year && (
                                                    <p className="text-sm text-gray-500">{cert.year}</p>
                                                )}
                                                {cert.description && (
                                                    <p className="text-sm text-gray-600 mt-1">{cert.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* References - NEW */}
                            {selectedCandidate.references?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">
                                        <i className="fas fa-address-book text-gradpath-green mr-2"></i>
                                        References
                                    </h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.references.map((ref, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{ref.name}</p>
                                                {ref.title && <p className="text-sm text-gray-600">{ref.title}</p>}
                                                {ref.company && <p className="text-sm text-gray-600">{ref.company}</p>}
                                                {(ref.email || ref.phone) && (
                                                    <div className="mt-1 text-sm text-gray-500">
                                                        {ref.email && <span>📧 {ref.email}</span>}
                                                        {ref.phone && <span className="ml-3">📱 {ref.phone}</span>}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Download CV Button - Always visible for applied candidates */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={handleDownloadCV}
                                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
                                >
                                    <i className="fas fa-file-pdf"></i> Download CV (PDF)
                                </button>
                                <p className="text-xs text-gray-400 text-center mt-1">
                                    A professionally formatted PDF will be downloaded
                                </p>
                            </div>

                            {/* Close Button */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <button
                                    onClick={closeProfileModal}
                                    className="w-full px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        {/* Hidden CV Template for PDF generation */}
                        <div className="hidden">
                            <CVPDFTemplate 
                                candidate={selectedCandidate} 
                                contactUnlocked={true}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AppliedCandidates;